import styled from '@emotion/styled'
import type { FC } from 'react'

import { useTheme } from '@emotion/react'

import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'
import { DataCollectIcon, DataScaleIcon, IntegrationIcon } from '@segment/matcha/components/Icons/Brandbook'

import IconWrapper from '@segment/matcha/components/IconWrapper'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

const copy = {
  heading: `Customizable solutions for developers`,
  description: `Don't get blocked by out of the box tools. Build what you need faster with flexible integrations and customizable tooling.`,
  boxes: [
    {
      icon: <DataCollectIcon />,
      title: `Collect data from anywhere and everywhere`,
    },
    {
      icon: <IntegrationIcon />,
      title: `Customize integrations or build new ones`,
    },
    {
      icon: <DataScaleIcon />,
      title: `Scale your customer data infrastructure as you grow`,
    },
  ],
}

const CustomizableSolutions: FC = () => {
  const theme = useTheme()

  return (
    <StyledSectionBase variant="noCopy">
      <Section.Contents>
        <StyledCopyTextBase centered={true}>
          <CopyText.Heading tag="h2">{copy.heading}</CopyText.Heading>
          <CopyText.Description tag="p" variant="pLarge">
            {copy.description}
          </CopyText.Description>
        </StyledCopyTextBase>
        <StyledColumnTrack
          columns={1}
          overrides={{
            md: 3,
          }}
        >
          {copy.boxes.map(({ icon, title }, key) => (
            <StyledFeatureBoxBase alignment="left" headerPosition="top" key={key}>
              <FeatureBox.Header>
                <IconWrapper
                  tokens={{
                    color: theme.palette.twilio.gray[100],
                  }}
                  background="white"
                  elevation={1}
                  variant="circle"
                >
                  {icon}
                </IconWrapper>
              </FeatureBox.Header>
              <FeatureBox.Body>
                <FeatureBox.Title variant="h4" tag="h3">
                  {title}
                </FeatureBox.Title>
              </FeatureBox.Body>
            </StyledFeatureBoxBase>
          ))}
        </StyledColumnTrack>
      </Section.Contents>
    </StyledSectionBase>
  )
}

const StyledFeatureBoxBase = styled(FeatureBoxBase)`
  ${({ theme }) => theme.breakpoints.down(`md`)} {
    text-align: center;
    justify-items: center;
    justify-content: center;
  }
`

const StyledSectionBase = styled(SectionBase)`
  ${Section.Wrapper} {
    padding-top: 0;
  }
`

const StyledColumnTrack = styled(ColumnTrack)`
  margin: 0 auto;
  max-width: 1120px;
  padding-top: 40px;

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    padding-top: 64px;
  }
`

const StyledCopyTextBase = styled(CopyTextBase)`
  max-width: 684px;
  margin: 0 auto;
`

export default CustomizableSolutions
